import React, { useEffect } from 'react';
import { X, User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';
import UserProfileContent from '../user/UserProfileContent';
import UserSettingsContent from '../user/UserSettingsContent';

interface UserPanelProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: 'profile' | 'settings';
  setActiveTab: (tab: 'profile' | 'settings') => void;
}

const UserPanel: React.FC<UserPanelProps> = ({
  isOpen,
  onClose,
  activeTab,
  setActiveTab
}) => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop - semi-transparent */}
      <div 
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel */}
      <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  activeTab === 'profile' 
                    ? 'bg-primary-100 text-primary-700 border border-primary-200' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                }`}
              >
                <User className="w-4 h-4" />
                <span>{t('userPanel.profile')}</span>
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  activeTab === 'settings' 
                    ? 'bg-primary-100 text-primary-700 border border-primary-200' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>{t('userPanel.settings')}</span>
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
              title={t('common.close')}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'profile' ? (
              <UserProfileContent />
            ) : (
              <UserSettingsContent />
            )}
          </div>

          {/* Footer with Logout Button */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('userPanel.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserPanel;